import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Inbox, ChevronRight } from "lucide-react";
import api from "@/lib/api";

/**
 * iter71e · Inbound · Triage from Vendors.
 *
 * Sibling to the InboundTriagePanel (member email replies) — this one
 * aggregates every unread vendor-authored message across the club's orders
 * and renders a compact triage strip so vendor messages never get lost
 * inside a single order timeline.
 *
 * Backend: GET /api/admin/vendor-messages/inbox
 */
export default function VendorInboxPanel() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    let mounted = true;
    const load = () => {
      api.get("/admin/vendor-messages/inbox")
        .then((r) => { if (mounted) setData(r.data); })
        .catch(() => { if (mounted) setError(true); });
    };
    load();
    const t = setInterval(load, 60_000);
    return () => { mounted = false; clearInterval(t); };
  }, []);

  if (error) return null;
  const total = data?.total_unread || 0;
  const threads = data?.threads || [];

  return (
    <section className="surface-card p-8 mt-8" data-testid="vendor-inbox-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline">Inbound · Triage from Vendors</div>
          <h2 className="font-serif text-2xl mt-1 text-[#1A1A1A]">Vendor Messages</h2>
          <p className="text-sm text-[#7A7A7A] mt-1">
            Every unread vendor reply threaded to an order, so nothing slips through the cracks.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-[10px] uppercase tracking-[0.22em] px-2 py-1 border ${
            total > 0
              ? "bg-[#1A422D] text-[#F9F8F4] border-[#1A422D]"
              : "text-[#4A4A4A] border-[#E5E3D8]"}`}
                data-testid="vendor-inbox-total">
            {total} unread
          </span>
          <button
            type="button"
            onClick={() => setCollapsed((v) => !v)}
            data-testid="vendor-inbox-toggle"
            className="btn-secondary text-xs"
          >
            {collapsed ? "Expand" : "Collapse"}
          </button>
        </div>
      </div>

      {!collapsed && (
        total === 0 ? (
          <div className="mt-6 p-6 border border-dashed border-[#E5E3D8] bg-[#FBFAF3] text-sm text-[#7A7A7A] flex items-center gap-3"
               data-testid="vendor-inbox-empty">
            <Inbox size={14} /> All caught up — no unread vendor messages.
          </div>
        ) : (
          <div className="mt-6 divide-y divide-[#E5E3D8]" data-testid="vendor-inbox-threads">
            {threads.map((t) => (
              <Link
                key={t.order_id}
                to={`/orders/${t.order_id}`}
                className="block p-4 hover:bg-[#F3F1E9]"
                data-testid={`vendor-inbox-thread-${t.order_id}`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-[240px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <MessageCircle size={13} className="text-[#8a6f33]" />
                      <span className="font-serif text-base text-[#1A422D]">
                        {t.order_number || t.order_id}
                      </span>
                      {t.confirmed_vendor && (
                        <span className="text-[10px] uppercase tracking-[0.16em] text-[#7A7A7A]">
                          · {t.confirmed_vendor}
                        </span>
                      )}
                    </div>
                    {(t.member_name || t.item_name) && (
                      <div className="text-xs text-[#4A4A4A] mt-1">
                        {t.member_name && <>Member: {t.member_name}{t.item_name ? " · " : ""}</>}
                        {t.item_name}
                      </div>
                    )}
                    <div className="mt-2 space-y-1">
                      {(t.messages || []).map((m) => (
                        <div key={m.message_id} className="text-sm text-[#1A1A1A]">
                          <span className="text-[10px] uppercase tracking-[0.16em] text-[#8a6f33] mr-2">
                            {m.author_name || "Vendor"}
                          </span>
                          <span className="text-[#4A4A4A]">{m.body}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] bg-[#1A422D] text-[#F9F8F4]"
                        data-testid={`vendor-inbox-count-${t.order_id}`}>
                    {t.unread_count} unread <ChevronRight size={11} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )
      )}
    </section>
  );
}
